import { useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

type UseUrlTabOptions = {
  paramName?: string;
  validTabs: string[];
  defaultTab?: string;
  replaceHistory?: boolean;
};

export function useUrlTab({
  paramName = "tab",
  validTabs,
  defaultTab,
  replaceHistory = true,
}: UseUrlTabOptions): [string, (tab: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlTab = searchParams.get(paramName);
  const firstValidTab = validTabs[0] || "";
  const resolvedDefault = defaultTab || firstValidTab;

  const activeTab =
    urlTab && validTabs.includes(urlTab) ? urlTab : resolvedDefault;

  useEffect(() => {
    if (validTabs.length === 0) return;

    if (!urlTab || !validTabs.includes(urlTab)) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(paramName, resolvedDefault);
          return params;
        },
        { replace: true }
      );
    }
  }, [urlTab, validTabs, resolvedDefault, paramName, setSearchParams]);

  const setActiveTab = useCallback(
    (tab: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(paramName, tab);
          return params;
        },
        { replace: replaceHistory }
      );
    },
    [paramName, setSearchParams, replaceHistory]
  );

  return [activeTab, setActiveTab];
}
